import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfidenceBadge,
  DataTable,
  Dialog,
  Field,
  Metric,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  ImpactAssessment,
  ImpactAssessmentDetail,
  ImpactItem,
  ImpactVerification,
  Regulation,
  RegulationVersion,
  RegulatoryAnalysisMethod,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, regulatoryApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, GovernedAction, useGovernedMutation } from "../../governance";
import {
  ASSESSMENT_STATUSES,
  AssessmentStatusBadge,
  DecisionBadge,
  ImpactBadge,
  ImpactVerificationBadge,
  MethodBadge,
  assessmentStatusLabel,
  isTerminal,
} from "./regulatoryLabels";

interface Member {
  id: string;
  email: string;
  displayName: string;
}

interface VersionChoice {
  version: RegulationVersion;
  regulation: Regulation;
}

// Impact assessments (APP-004 R3 P4).
//
// The item list is the backend's. Classification comes from the deterministic
// pass over the diff, so an AI written narrative changes the summary and
// nothing else, and confidence is shown as lexical overlap rather than as a
// reason a status is what it is. Verification is a person's decision, taken by
// somebody other than the author, and it is append only.
export function GovImpact() {
  const [params, setParams] = useSearchParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const statusFilter = params.get("status") ?? "";
  const selected = params.get("assessment") ?? "";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const assessments = useAsync<ImpactAssessment[]>(
    async () => (await regulatoryApi.assessments(orgId)).data.assessments,
    [orgId],
  );
  const choices = useAsync<VersionChoice[]>(async () => {
    const regulations = (await regulatoryApi.regulations(orgId)).data.regulations;
    return (
      await Promise.all(
        regulations.map(async (regulation) =>
          (await regulatoryApi.versions(orgId, regulation.id)).data.versions.map((version) => ({
            version,
            regulation,
          })),
        ),
      )
    ).flat();
  }, [orgId]);
  const detail = useAsync<ImpactAssessmentDetail | null>(
    async () => (selected ? (await regulatoryApi.assessment(orgId, selected)).data.assessment : null),
    [orgId, selected],
  );
  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);

  const versionLabel = (id: string) => {
    const match = (choices.data ?? []).find((c) => c.version.id === id);
    return match ? `${match.regulation.name} ${match.version.versionLabel}` : id;
  };

  const rows = (assessments.data ?? []).filter((a) => !statusFilter || a.status === statusFilter);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Impact assessments</h1>
          <p>
            What a change to a regulation means for the policies, requirements, mappings, evidence
            and exceptions already on file. The item list and every classification are computed by
            the backend from the deterministic diff, and each item stays unverified until a person
            other than the author decides on it.{" "}
            <Link to="/studio/governance/remediation">Remediation</Link>
          </p>
        </div>
        <div className="badge-row">
          <GenerateAssessment
            orgId={orgId}
            choices={choices.data ?? []}
            onDone={(id) => {
              assessments.reload();
              setParam("assessment", id);
            }}
          />
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="filter-row">
            <Field label="Status">
              {(id) => (
                <Select id={id} value={statusFilter} onChange={(e) => setParam("status", e.target.value)}>
                  <option value="">Any status</option>
                  {ASSESSMENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {assessmentStatusLabel(s)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </CardBody>
      </Card>

      <GovPanel
        title="Assessments"
        state={assessments}
        loadingLabel="Loading assessments…"
        isEmpty={() => rows.length === 0}
        emptyTitle="No assessments match"
        emptyDescription="Generate one from two versions of a regulation you have imported."
      >
        {() => (
          <DataTable
            caption="Impact assessments"
            onRowClick={(row) => setParam("assessment", row.id)}
            columns={[
              {
                key: "title",
                header: "Assessment",
                render: (row) => (
                  <>
                    <strong>{row.title}</strong>
                    <div className="badge-row">
                      <AssessmentStatusBadge status={row.status} />
                      <MethodBadge method={row.method} />
                      {row.id === selected ? <Badge tone="info">Shown below</Badge> : null}
                    </div>
                  </>
                ),
              },
              {
                key: "version",
                header: "Regulation version",
                render: (row) => versionLabel(row.regulationVersionId),
              },
              { key: "items", header: "Items", render: (row) => row.itemCount },
              {
                key: "unmatched",
                header: "Unresolved sections",
                render: (row) =>
                  row.unmatchedSections.length === 0 ? (
                    <span className="muted-note">None</span>
                  ) : (
                    <Badge tone="warning">{row.unmatchedSections.length} unresolved</Badge>
                  ),
              },
              { key: "created", header: "Generated", render: (row) => formatDate(row.createdAt) },
            ]}
            rows={rows}
          />
        )}
      </GovPanel>

      {selected ? (
        <GovPanel
          title="Assessment"
          state={detail}
          loadingLabel="Loading the assessment…"
          isEmpty={(data) => data === null}
          emptyTitle="Assessment not found"
        >
          {(data) =>
            data === null ? null : (
              <AssessmentDetail
                orgId={orgId}
                assessment={data}
                members={members.data ?? []}
                versionLabel={versionLabel}
                onChanged={() => {
                  detail.reload();
                  assessments.reload();
                }}
              />
            )
          }
        </GovPanel>
      ) : null}
    </>
  );
}

function AssessmentDetail({
  orgId,
  assessment,
  members,
  versionLabel,
  onChanged,
}: {
  orgId: string;
  assessment: ImpactAssessmentDetail;
  members: Member[];
  versionLabel: (id: string) => string;
  onChanged: () => void;
}) {
  const terminal = isTerminal(assessment.status);
  const undecided = assessment.items.filter((i) => i.verificationState === "unverified").length;

  return (
    <>
      <div className="badge-row">
        <AssessmentStatusBadge status={assessment.status} />
        <MethodBadge method={assessment.method} />
      </div>
      <h3>{assessment.title}</h3>
      <p className="muted-note">
        {versionLabel(assessment.regulationVersionId)} · generated {formatDate(assessment.createdAt)}
      </p>
      <p>{assessment.summary}</p>
      <p className="muted-note">
        Classification produced by <code>{assessment.model}</code> {assessment.analyzerVersion}.
        {assessment.method === "ai_assisted"
          ? " The summary above was written by a model. It does not set any item's impact or verification state, both of which are deterministic and human decided."
          : " No model wrote any part of this assessment."}
      </p>

      <div className="metric-grid">
        <div className="metric-card">
          <Metric value={assessment.items.length} label="Impact items" />
        </div>
        <div className="metric-card">
          <Metric value={undecided} label="Awaiting a decision" />
        </div>
        <div className="metric-card">
          <Metric value={assessment.unmatchedSections.length} label="Unresolved sections" />
        </div>
      </div>

      <div className="badge-row">
        {assessment.status === "draft" ? (
          <GovernedAction
            permission={GOVERNANCE_PERMISSIONS.regulationsManage}
            label="Submit for review"
            title="Submit this assessment for review"
            description="Moves the assessment into review so its items can be decided on."
            consequence="Items must each be verified or rejected before the assessment can be accepted."
            confirmLabel="Submit"
            busyLabel="Submitting…"
            triggerVariant="primary"
            onConfirm={() => regulatoryApi.setAssessmentStatus(orgId, assessment.id, "in_review")}
            onDone={onChanged}
          />
        ) : null}
        {!terminal ? (
          <>
            <GovernedAction
              permission={GOVERNANCE_PERMISSIONS.regulationsManage}
              label="Accept assessment"
              title="Accept this assessment"
              description="Records that the organization has taken this assessment as its answer to the change."
              consequence="Accepting is final. An accepted assessment cannot be reopened, and it is refused while any item is still unverified."
              confirmLabel="Accept"
              busyLabel="Accepting…"
              onConfirm={() => regulatoryApi.setAssessmentStatus(orgId, assessment.id, "accepted")}
              onDone={onChanged}
            />
            <GovernedAction
              permission={GOVERNANCE_PERMISSIONS.regulationsManage}
              label="Dismiss assessment"
              title="Dismiss this assessment"
              description="Records that this change needs nothing from the organization."
              consequence="Dismissing is final. A dismissed assessment cannot be reopened."
              confirmLabel="Dismiss"
              busyLabel="Dismissing…"
              destructive
              onConfirm={() => regulatoryApi.setAssessmentStatus(orgId, assessment.id, "dismissed")}
              onDone={onChanged}
            />
          </>
        ) : (
          <span className="muted-note" data-testid="assessment-terminal">
            This assessment is {assessmentStatusLabel(assessment.status).toLowerCase()} and closed.
            Generate a new assessment if the change needs looking at again.
          </span>
        )}
      </div>

      {assessment.unmatchedSections.length > 0 ? (
        <Card>
          <CardHeader title="Unresolved sections" />
          <CardBody>
            <p className="muted-note">
              These sections changed and nothing on file matched them. An absent item is an open
              question, not a finding of no impact.
            </p>
            <div className="badge-row">
              {assessment.unmatchedSections.map((key) => (
                <Badge tone="warning" key={key}>
                  {key}
                </Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {assessment.items.map((item) => (
        <ImpactItemCard
          key={item.id}
          orgId={orgId}
          item={item}
          members={members}
          terminal={terminal}
          onChanged={onChanged}
        />
      ))}
    </>
  );
}

function ImpactItemCard({
  orgId,
  item,
  members,
  terminal,
  onChanged,
}: {
  orgId: string;
  item: ImpactItem;
  members: Member[];
  terminal: boolean;
  onChanged: () => void;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const history = useAsync<ImpactVerification[]>(
    async () => (showHistory ? (await regulatoryApi.itemHistory(orgId, item.id)).data.history : []),
    [orgId, item.id, showHistory],
  );
  const memberName = (id: string) => {
    const match = members.find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const decision = (verdict: "verified" | "rejected" | "reopened") => (rationale: string) =>
    regulatoryApi.decideItem(orgId, item.id, { decision: verdict, rationale });

  return (
    <Card>
      <CardHeader
        title={item.assetLabel}
        action={
          <div className="badge-row">
            <ImpactBadge impact={item.impact} />
            <ImpactVerificationBadge state={item.verificationState} />
          </div>
        }
      />
      <CardBody>
        <p className="muted-note">
          {item.assetType} · section <code>{item.sectionKey}</code> · <code>{item.assetId}</code>
        </p>
        <p>{item.rationale}</p>
        <p className="muted-note">
          Overlap with the changed obligation <ConfidenceBadge value={item.confidence} />. This is
          how strongly the wording matched, not a decision: it does not set the impact above and it
          does not stand in for verification.
        </p>
        {item.citations.length > 0 ? (
          <ul className="muted-note">
            {item.citations.map((citation) => (
              <li key={`${citation.sourceType}-${citation.sourceId}`}>
                {citation.sourceType} <code>{citation.sourceId}</code>: {citation.excerpt}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="muted-note">
          {item.verificationState === "unverified" ? (
            "Nobody has decided on this item yet."
          ) : (
            <>
              {item.verificationState === "verified" ? "Verified" : "Rejected"} by{" "}
              {memberName(item.verifiedBy ?? "")} on {formatDate(item.verifiedAt)}.
            </>
          )}
        </p>

        {!terminal ? (
          <div className="badge-row">
            {item.verificationState !== "verified" ? (
              <GovernedAction
                permission={GOVERNANCE_PERMISSIONS.impactVerify}
                label="Verify"
                title="Verify this impact item"
                description="Confirms that this asset really is affected as classified."
                consequence="The decision is recorded permanently. The person who generated the assessment cannot verify its items."
                confirmLabel="Verify"
                busyLabel="Recording…"
                requireRationale
                rationaleLabel="What you checked"
                onConfirm={decision("verified")}
                onDone={onChanged}
              />
            ) : null}
            {item.verificationState !== "rejected" ? (
              <GovernedAction
                permission={GOVERNANCE_PERMISSIONS.impactVerify}
                label="Reject"
                title="Reject this impact item"
                description="Records that this asset is not affected after all."
                consequence="The decision is recorded permanently. A rejected item can be reopened, which returns it to unverified."
                confirmLabel="Reject"
                busyLabel="Recording…"
                destructive
                requireRationale
                rationaleLabel="Why it does not apply"
                onConfirm={decision("rejected")}
                onDone={onChanged}
              />
            ) : null}
            {item.verificationState !== "unverified" ? (
              <GovernedAction
                permission={GOVERNANCE_PERMISSIONS.impactVerify}
                label="Reopen"
                title="Reopen this impact item"
                description="Returns the item to unverified so it can be decided again."
                consequence="The earlier decision stays in the history. Reopening does not erase it."
                confirmLabel="Reopen"
                busyLabel="Reopening…"
                requireRationale
                rationaleLabel="Why it needs another look"
                onConfirm={decision("reopened")}
                onDone={onChanged}
              />
            ) : null}
            {item.verificationState === "verified" ? (
              <AssignRemediation orgId={orgId} item={item} members={members} onDone={onChanged} />
            ) : (
              <span className="muted-note">
                Remediation can be assigned once this item is verified.
              </span>
            )}
          </div>
        ) : null}

        <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>
          {showHistory ? "Hide history" : "Verification history"}
        </Button>
        {showHistory ? (
          <GovPanel
            bare
            state={history}
            loadingLabel="Loading history…"
            isEmpty={(rows) => rows.length === 0}
            emptyTitle="No decisions yet"
          >
            {(rows) => (
              <DataTable
                caption="Verification history"
                columns={[
                  {
                    key: "decision",
                    header: "Decision",
                    render: (row) => <DecisionBadge decision={row.decision} />,
                  },
                  {
                    key: "states",
                    header: "State",
                    render: (row) => `${row.fromState} to ${row.toState}`,
                  },
                  { key: "actor", header: "Decided by", render: (row) => memberName(row.actorId) },
                  { key: "at", header: "When", render: (row) => formatDate(row.decidedAt) },
                  { key: "why", header: "Rationale", render: (row) => row.rationale || "Not stated" },
                ]}
                rows={rows}
              />
            )}
          </GovPanel>
        ) : null}
      </CardBody>
    </Card>
  );
}

function AssignRemediation({
  orgId,
  item,
  members,
  onDone,
}: {
  orgId: string;
  item: ImpactItem;
  members: Member[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(`Act on ${item.assetLabel}`);
  const [assigneeId, setAssignee] = useState("");
  const [dueAt, setDue] = useState("");
  const [notes, setNotes] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.regulationsManage);

  const submit = async () => {
    const ok = await mutation.run(() =>
      regulatoryApi.assignItem(orgId, item.id, {
        title: title.trim(),
        assigneeId,
        dueAt: dueAt || null,
        notes: notes.trim(),
      }),
    );
    if (!ok) return;
    setOpen(false);
    onDone();
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Assign remediation
      </Button>
      <Dialog
        open={open}
        title="Assign remediation"
        description="Creates tracked work from this verified item."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={title.trim().length === 0 || assigneeId === "" || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Assigning…" : "Assign"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Title">
          {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field label="Owner">
          {(id) => (
            <Select id={id} value={assigneeId} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Choose an owner</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Due">
          {(id) => (
            <TextInput id={id} type="date" value={dueAt} onChange={(e) => setDue(e.target.value)} />
          )}
        </Field>
        <Field label="Notes">
          {(id) => (
            <TextArea id={id} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          )}
        </Field>
      </Dialog>
    </>
  );
}

function GenerateAssessment({
  orgId,
  choices,
  onDone,
}: {
  orgId: string;
  choices: VersionChoice[];
  onDone: (assessmentId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [fromVersionId, setFrom] = useState("");
  const [toVersionId, setTo] = useState("");
  const [title, setTitle] = useState("");
  const [method, setMethod] = useState<RegulatoryAnalysisMethod>("deterministic");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.regulationsManage);

  const submit = async () => {
    let created: string | null = null;
    const ok = await mutation.run(async () => {
      const res = await regulatoryApi.createAssessment(orgId, {
        fromVersionId,
        toVersionId,
        title: title.trim(),
        method,
      });
      created = res.data.assessment.id;
      return res;
    });
    if (!ok || created === null) return;
    setOpen(false);
    setTitle("");
    onDone(created);
  };

  const option = (choice: VersionChoice) => (
    <option key={choice.version.id} value={choice.version.id}>
      {choice.regulation.name} {choice.version.versionLabel}
    </option>
  );

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        Generate assessment
      </Button>
      <Dialog
        open={open}
        title="Generate an impact assessment"
        description="Compares two versions of one regulation and works out what on file is affected."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                fromVersionId === "" || toVersionId === "" || title.trim().length === 0 || mutation.busy
              }
              onClick={() => void submit()}
            >
              {mutation.busy ? "Generating…" : "Generate"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Title">
          {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field label="From version">
          {(id) => (
            <Select id={id} value={fromVersionId} onChange={(e) => setFrom(e.target.value)}>
              <option value="">Choose a version</option>
              {choices.map(option)}
            </Select>
          )}
        </Field>
        <Field label="To version">
          {(id) => (
            <Select id={id} value={toVersionId} onChange={(e) => setTo(e.target.value)}>
              <option value="">Choose a version</option>
              {choices.map(option)}
            </Select>
          )}
        </Field>
        <Field
          label="Narrative"
          help="Both settings produce the same items and the same classifications. The choice only decides who writes the summary."
        >
          {(id) => (
            <Select
              id={id}
              value={method}
              onChange={(e) => setMethod(e.target.value as RegulatoryAnalysisMethod)}
            >
              <option value="deterministic">Deterministic summary only</option>
              <option value="ai_assisted">Let a model write the summary</option>
            </Select>
          )}
        </Field>
      </Dialog>
    </>
  );
}
